import React, { useState } from 'react';
import API_BASE_URL from './config';

const OnboardingStep6 = ({ formData, onBack }) => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async () => {
        setSubmitting(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/onboarding/complete`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to complete onboarding');
            }

            const data = await response.json();
            setSuccess(true);
            
            // Redirect to dashboard after short delay
            setTimeout(() => {
                window.location.href = '/';
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to complete onboarding');
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="wizard-step">
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <div style={{ fontSize: '72px', marginBottom: '20px' }}>✅</div>
                    <h2 style={{ color: 'var(--text-primary)' }}>Setup Complete!</h2>
                    <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginTop: '20px' }}>
                        Welcome to RFQ Tracker! Redirecting to your dashboard...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="wizard-step">
            <div className="step-header">
                <h2>Review & Complete Setup</h2>
                <p>Please review your information before submitting</p>
            </div>

            <div style={{ marginBottom: '30px' }}>
                {/* Company Info Review */}
                <div
                    style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}
                >
                    <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-primary)' }}>Company Information</h3>
                    <table style={{ width: '100%', fontSize: '14px', color: 'var(--text-secondary)' }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: '5px 0', fontWeight: '600', width: '150px', color: 'var(--text-primary)' }}>Company Name:</td>
                                <td style={{ padding: '5px 0' }}>{formData.company.display_name}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '5px 0', fontWeight: '600', color: 'var(--text-primary)' }}>Address:</td>
                                <td style={{ padding: '5px 0' }}>{formData.company.address}</td>
                            </tr>
                            {formData.company.tagline && (
                                <tr>
                                    <td style={{ padding: '5px 0', fontWeight: '600', color: 'var(--text-primary)' }}>Tagline:</td>
                                    <td style={{ padding: '5px 0' }}>{formData.company.tagline}</td>
                                </tr>
                            )}
                            <tr>
                                <td style={{ padding: '5px 0', fontWeight: '600', color: 'var(--text-primary)' }}>Work Types:</td>
                                <td style={{ padding: '5px 0' }}>{formData.company.types_of_work.join(', ')}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '5px 0', fontWeight: '600', color: 'var(--text-primary)', verticalAlign: 'top' }}>Logo:</td>
                                <td style={{ padding: '5px 0' }}>
                                    {formData.logo_url ? (
                                        <div>
                                            <img
                                                src={`${API_BASE_URL}${formData.logo_url}`}
                                                alt="Company logo preview"
                                                style={{
                                                    maxWidth: '150px',
                                                    maxHeight: '100px',
                                                    objectFit: 'contain',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    padding: '5px',
                                                    background: '#fff'
                                                }}
                                            />
                                            <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--success-color, #28a745)' }}>
                                                ✓ Logo uploaded
                                            </div>
                                        </div>
                                    ) : (
                                        <span style={{ color: 'var(--text-muted)' }}>Not uploaded</span>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Cities Review */}
                <div
                    style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}
                >
                    <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-primary)' }}>
                        Locations ({formData.city_ids.length} cities selected)
                    </h3>
                    <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                        {formData.city_ids.length} cities will be tracked for RFQs
                    </p>
                </div>

                {/* Users Review */}
                <div
                    style={{
                        padding: '20px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '8px',
                        marginBottom: '20px'
                    }}
                >
                    <h3 style={{ marginTop: 0, marginBottom: '15px', color: 'var(--text-primary)' }}>
                        Team Members ({formData.users.length} users)
                    </h3>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse', color: 'var(--text-secondary)' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-tertiary, var(--bg-secondary))' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'var(--text-primary)' }}>Name</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'var(--text-primary)' }}>Email</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'var(--text-primary)' }}>Role</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'var(--text-primary)' }}>Region</th>
                                    <th style={{ padding: '8px', textAlign: 'center', color: 'var(--text-primary)' }}>Invite</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.users.map((user, index) => (
                                    <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                        <td style={{ padding: '8px' }}>{user.full_name}</td>
                                        <td style={{ padding: '8px' }}>{user.email}</td>
                                        <td style={{ padding: '8px' }}>{user.role}</td>
                                        <td style={{ padding: '8px' }}>{user.assigned_region}</td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                            {user.send_invite ? '✓' : 'Later'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Contact Import Info */}
                <div
                    style={{
                        padding: '20px',
                        background: 'var(--bg-warning, #fff3cd)',
                        borderRadius: '8px',
                        border: '1px solid var(--border-warning, #ffc107)'
                    }}
                >
                    <h4 style={{ marginTop: 0, color: 'var(--text-primary)' }}>📥 Next Steps After Setup:</h4>
                    <ul style={{ marginLeft: '20px', color: 'var(--text-secondary)' }}>
                        <li>Import your contacts from the Contacts menu</li>
                        <li>Run the scraper to pull in RFQs from your selected cities</li>
                        <li>Invite additional team members as needed</li>
                    </ul>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="step-footer">
                <button onClick={onBack} className="btn-back" disabled={submitting}>
                    ← Back
                </button>
                <button
                    onClick={handleSubmit}
                    className="btn-submit"
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <span className="loading-spinner"></span> Completing Setup...
                        </>
                    ) : (
                        'Complete Setup ✓'
                    )}
                </button>
            </div>
        </div>
    );
};

export default OnboardingStep6;

